import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BellPlus, X, SlidersHorizontal, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { sharedApi } from "@/lib/api";
import { useCategories } from "@/hooks/useCategories";
import { useLocations } from "@/hooks/useLocations";
import { SEARCH } from "@/constants/testIds";

/**
 * SchemaFilterRail (Iter-238) — renders the browse filter sidebar entirely
 * from `GET /api/classifieds/filter-schema?slug=X&facets=true`. No filters
 * are hardcoded per-category anymore; the backend is the single source of
 * truth (spec: SPEC_FILTERS_ITER238).
 *
 * URL-driven state: parent (SearchPage) owns the URLSearchParams and
 * passes `params` (URLSearchParams) + `onApply(patch)` for updates. Every
 * change writes to the URL so filtered pages stay shareable/SEO-crawlable.
 *
 * Facet counts: when the endpoint returns a `facets` block, count labels
 * render next to each option and zero-count options are visually dimmed.
 */
export const CONDITION_LEGACY_ALIAS = { new: "brand-new", used: "good" };

function FilterGroup({ label, children }) {
  return (
    <div data-refine-section={String(label).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}>
      <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-2">
        {label}
      </div>
      {children}
    </div>
  );
}

/**
 * Multi-select chips with optional live counts. Values are stored as a
 * comma-separated string in the URL param.
 */
function ChipsMulti({ testId, value, onChange, options, counts, disabledZero }) {
  const selected = new Set((value || "").split(",").filter(Boolean));
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {options.map((opt) => {
        const on = selected.has(opt);
        const count = counts ? counts[opt] : undefined;
        const zero = disabledZero && counts && !count;
        return (
          <button
            key={opt}
            type="button"
            aria-pressed={on}
            disabled={zero}
            data-testid={`${testId}-${slugify(opt)}`}
            onClick={() => {
              const next = new Set(selected);
              if (on) next.delete(opt);
              else next.add(opt);
              onChange(Array.from(next).join(","));
            }}
            className={`rounded-full px-2.5 py-1 text-[11px] font-semibold border transition-colors ${
              on
                ? "bg-[color:var(--brand-blue)] text-white border-transparent"
                : zero
                ? "bg-slate-50 text-slate-300 border-transparent cursor-not-allowed"
                : "bg-white text-slate-700 border-soft hover:border-[color:var(--brand-blue)]"
            }`}
          >
            {opt}
            {counts && count != null && (
              <span className="ml-1 opacity-75">({count})</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function slugify(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function SingleChips({ testId, value, onChange, options }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {options.map((opt) => {
        const on = value === opt;
        return (
          <button
            key={opt}
            type="button"
            aria-pressed={on}
            data-testid={`${testId}-${slugify(opt)}`}
            onClick={() => onChange(on ? "" : opt)}
            className={`rounded-full px-2.5 py-1 text-[11px] font-semibold border transition-colors ${
              on
                ? "bg-[color:var(--brand-blue)] text-white border-transparent"
                : "bg-white text-slate-700 border-soft hover:border-[color:var(--brand-blue)]"
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function MakeSelect({ endpoint, categorySlug, value, onChange, testId, counts }) {
  const q = useQuery({
    queryKey: ["filter-schema-makes", endpoint, categorySlug],
    queryFn: async () => {
      const path = endpoint.replace(/^\/api/, "");
      const { data } = await sharedApi.get(path, {
        params: categorySlug ? { category_slug: categorySlug } : undefined,
      });
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.items)) return data.items;
      if (Array.isArray(data?.makes)) return data.makes;
      return [];
    },
    enabled: !!endpoint,
    staleTime: 10 * 60_000,
  });
  const opts = (q.data || []).map((o) =>
    typeof o === "string"
      ? { value: o, label: o }
      : {
          value: o.value || o.slug || o.name,
          label: o.label || o.name || o.value,
          // Iter 784 — /makes items now ship live-stock counts.
          count: o.count,
        }
  );
  return (
    <select
      data-testid={testId}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="input"
    >
      <option value="">Any make</option>
      {opts.map((o) => {
        const cnt = o.count ?? counts?.[o.value] ?? counts?.[o.value?.toLowerCase?.()];
        return (
          <option key={o.value} value={o.value}>
            {`${o.label}${cnt != null ? ` (${cnt})` : ""}`}
          </option>
        );
      })}
    </select>
  );
}

/** Iter 784 — model dropdown fed by live stock
 *  (`GET /classifieds/models?category_slug&make`). Falls back to the old
 *  free-text input while no make is picked or when stock has no models
 *  for the make. */
function ModelSelect({ categorySlug, make, value, onChange }) {
  const q = useQuery({
    queryKey: ["filter-schema-models", categorySlug, make],
    enabled: !!make,
    staleTime: 10 * 60_000,
    retry: 0,
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/models", {
        params: { category_slug: categorySlug, make },
      });
      if (Array.isArray(data?.items) && data.items.length) return data.items;
      if (Array.isArray(data?.models)) return data.models.map((m) => ({ name: m }));
      return [];
    },
  });
  const items = q.data || [];
  const hasList = !!make && items.length > 0;
  if (!hasList) {
    return (
      <input
        data-testid="filter-model"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input"
        placeholder={make ? `Search models within ${make}` : "Pick a make first"}
        disabled={!make}
      />
    );
  }
  // Keep a stored model visible even if stock no longer lists it.
  const known = items.some((m) => (m.name || "").toLowerCase() === (value || "").toLowerCase());
  return (
    <select
      data-testid="filter-model"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="input"
    >
      <option value="">Any model</option>
      {!known && value && <option value={value}>{value}</option>}
      {items.map((m) => (
        <option key={m.name} value={m.name}>
          {`${m.name}${m.count != null ? ` (${m.count})` : ""}`}
        </option>
      ))}
    </select>
  );
}

export default function SchemaFilterRail({ params, categorySlug, onApply }) {
  const { data: categories = [] } = useCategories();
  const { data: regions = [] } = useLocations();

  // Pull the (schema, facets) bundle whenever the category slug changes.
  // Fine to fetch with facets=true; when the slug is empty we skip.
  const schemaQ = useQuery({
    queryKey: ["filter-schema", categorySlug],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/filter-schema", {
        params: { slug: categorySlug, facets: true },
      });
      return data;
    },
    enabled: !!categorySlug,
    staleTime: 60_000,
  });
  const schema = schemaQ.data || null;
  const facets = schema?.facets || null;

  // Local editable state — synchronised with URL params so the "Apply"
  // button isn't required; every control writes-through onApply().
  const readParam = (k) => params.get(k) || "";
  const [state, setState] = useState(() => paramsToState(params));

  // Mobile collapse (default closed so phones see listings immediately).
  const [mobileOpen, setMobileOpen] = useState(false);
  const activeFilterCount = useMemo(() => {
    let n = 0;
    params.forEach((v, k) => {
      if (v && k !== "category" && k !== "page" && k !== "sort" && k !== "q") n += 1;
    });
    return n;
  }, [params]);

  useEffect(() => {
    setState(paramsToState(params));
  }, [params]);

  const cities = useMemo(() => {
    const r = regions.find((x) => x.region === state.region);
    return r?.cities || [];
  }, [state.region, regions]);

  const set = (patch) => {
    const next = { ...state, ...patch };
    setState(next);
    onApply(patch);
  };

  // Iter-372 — open-home tri-state, Property category only.
  const isProperty = (categorySlug || "").startsWith("property");
  const openHomeGroup = isProperty ? (
    <FilterGroup label="Open homes">
      <div className="flex flex-wrap gap-1.5" data-testid="filter-open-home">
        {[
          { value: "", label: "All" },
          { value: "yes", label: "Has open home" },
          { value: "weekend", label: "This weekend" },
        ].map((opt) => {
          const on = (state.open_home || "") === opt.value;
          return (
            <button
              key={opt.value || "all"}
              type="button"
              aria-pressed={on}
              data-testid={`filter-open-home-${opt.value || "all"}`}
              onClick={() => set({ open_home: opt.value })}
              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold border transition-colors ${
                on
                  ? "bg-[color:var(--brand-blue)] text-white border-transparent"
                  : "bg-white text-slate-600 border-soft hover:border-[color:var(--brand-blue)]"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </FilterGroup>
  ) : null;

  const clear = () => {
    // Preserve category (user is inside a category page context) and
    // reset everything else.
    const preserve = { category: state.category };
    setState({ ...emptyState(), ...preserve });
    onApply({
      q: "",
      region: "",
      city: "",
      area: "",
      min_price: "",
      max_price: "",
      condition: "",
      sort: "",
      make: "",
      model: "",
      min_year: "",
      max_year: "",
      open_home: "",
      seller_type: "",
      bg_check: "",
      // Also clear every attr.* / range / history param the schema exposes.
      ...Object.fromEntries(
        (schema?.attribute_filters || []).map((f) => [f.param, ""])
      ),
      ...Object.fromEntries(
        (schema?.range_filters || []).flatMap((f) => [[f.param_min, ""], [f.param_max, ""]])
      ),
      ...Object.fromEntries(
        (schema?.history_filters || []).map((f) => [f.param, ""])
      ),
    });
  };

  // Static fallback filters when no category is selected (site-wide search).
  const staticCommon = (
    <>
      <FilterGroup label="Category">
        <select
          data-testid={SEARCH.filterCategory}
          value={state.category}
          onChange={(e) => set({ category: e.target.value })}
          className="input"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <optgroup key={c.id} label={c.name}>
              <option value={c.slug}>{`All in ${c.name}`}</option>
              {(c.children || []).map((sc) => (
                <option key={sc.id} value={sc.slug}>{sc.name}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </FilterGroup>

      <FilterGroup label="Region">
        <select
          data-testid={SEARCH.filterRegion}
          value={state.region}
          onChange={(e) => set({ region: e.target.value, city: "" })}
          className="input"
        >
          <option value="">All of NZ</option>
          {regions.map((r) => (
            <option key={r.region} value={r.region}>{r.region}</option>
          ))}
        </select>
      </FilterGroup>

      {cities.length > 0 && (
        <FilterGroup label="City">
          <select
            data-testid={SEARCH.filterCity}
            value={state.city}
            onChange={(e) => set({ city: e.target.value })}
            className="input"
          >
            <option value="">All cities</option>
            {cities.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </FilterGroup>
      )}

      <FilterGroup label="Price ($)">
        <div className="flex items-center gap-2">
          <input
            data-testid={SEARCH.filterMinPrice}
            value={state.min_price}
            onChange={(e) => set({ min_price: e.target.value.replace(/[^0-9]/g, "") })}
            type="text"
            placeholder="Min"
            className="input flex-1"
          />
          <span className="text-slate-300">—</span>
          <input
            data-testid={SEARCH.filterMaxPrice}
            value={state.max_price}
            onChange={(e) => set({ max_price: e.target.value.replace(/[^0-9]/g, "") })}
            type="text"
            placeholder="Max"
            className="input flex-1"
          />
        </div>
      </FilterGroup>

      <FilterGroup label="Sort">
        <select
          data-testid={SEARCH.filterSort}
          value={state.sort || "recent"}
          onChange={(e) => set({ sort: e.target.value === "recent" ? "" : e.target.value })}
          className="input"
        >
          <option value="recent">Newest first</option>
          <option value="price_asc">Price: low → high</option>
          <option value="price_desc">Price: high → low</option>
          {/* Iter-356 — display-only: "distance" is set via the contextual
              Sort-by-nearest button (needs granted location), never picked
              cold from this select. */}
          {state.sort === "distance" && <option value="distance">Nearest first</option>}
        </select>
      </FilterGroup>

      {openHomeGroup}
    </>
  );

  const schemaCommon = schema?.common_filters || [];
  const schemaVehicle = schema?.vehicle_filters || [];
  const isVehicle = schemaVehicle.length > 0;
  // Iter-531 — 4WD is a standalone checkbox on vehicles; hide the raw
  // "Wheel Drive" attribute section (the checkbox represents it).
  const schemaAttrs = (schema?.attribute_filters || []).filter(
    (f) => !(isVehicle && f.key === "Wheel Drive"),
  );
  const schemaRanges = schema?.range_filters || [];
  const schemaHistory = schema?.history_filters || [];
  const sellerTypeFilter = schema?.seller_type_filter || null;
  // Iter-241 hardening: admin-created categories with no attribute specs
  // yet may return a schema whose `common_filters` is missing or empty
  // (bulletin's `{filters: []}` shorthand). Fall back to the static
  // site-wide filter set so users still get price/region/condition/sort
  // — never render an empty rail.
  const useFallbackStatic = !!schema && schemaCommon.length === 0;
  const areaOptions = getAreaOptions(schema, state.region, state.city);

  const renderCommonField = (f) => {
    switch (f.key) {
      case "q":
        return (
          <FilterGroup key="q" label={f.label}>
            <input
              data-testid="filter-q"
              value={state.q}
              onChange={(e) => set({ q: e.target.value })}
              className="input"
              placeholder="Search within results"
            />
          </FilterGroup>
        );
      case "region":
        return (
          <FilterGroup key="region" label={f.label}>
            <select
              data-testid={SEARCH.filterRegion}
              value={state.region}
              onChange={(e) => set({ region: e.target.value, city: "", area: "" })}
              className="input"
            >
              <option value="">All of NZ</option>
              {regions.map((r) => (
                <option key={r.region} value={r.region}>{r.region}</option>
              ))}
            </select>
          </FilterGroup>
        );
      case "city":
        if (!cities.length) return null;
        return (
          <FilterGroup key="city" label={f.label}>
            <select
              data-testid={SEARCH.filterCity}
              value={state.city}
              onChange={(e) => set({ city: e.target.value, area: "" })}
              className="input"
            >
              <option value="">All cities</option>
              {cities.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </FilterGroup>
        );
      case "area":
        return (
          <FilterGroup key="area" label={f.label}>
            <input
              data-testid="filter-area"
              value={state.area}
              onChange={(e) => set({ area: e.target.value })}
              className="input"
              list="area-options"
              placeholder="e.g. Ponsonby"
            />
            {!!areaOptions.length && (
              <datalist id="area-options">
                {areaOptions.map((a) => <option key={a} value={a} />)}
              </datalist>
            )}
          </FilterGroup>
        );
      case "min_price":
      case "max_price":
        // Rendered together once below to preserve range UX.
        return null;
      case "condition":
        return (
          <FilterGroup key="condition" label={f.label}>
            <ChipsMulti
              testId="filter-condition"
              value={state.condition}
              onChange={(v) => set({ condition: v })}
              options={(f.options || []).filter((o) => !(o in CONDITION_LEGACY_ALIAS))}
            />
          </FilterGroup>
        );
      case "sort":
        return (
          <FilterGroup key="sort" label={f.label}>
            <SingleChips
              testId={SEARCH.filterSort}
              value={state.sort}
              onChange={(v) => set({ sort: v })}
              options={f.options || []}
            />
          </FilterGroup>
        );
      default:
        return null;
    }
  };

  return (
    <aside
      data-testid={SEARCH.filterRail}
      className="w-full lg:w-72 shrink-0 lg:sticky lg:top-28 self-start"
    >
      {/* Mobile-only collapse toggle — on phones the rail defaults closed so
          listings appear right away instead of ~2,000px of filters first.
          Desktop (lg+) always shows the rail; the button is hidden. */}
      <button
        type="button"
        data-testid="filter-rail-mobile-toggle"
        onClick={() => setMobileOpen((s) => !s)}
        aria-expanded={mobileOpen}
        className="lg:hidden w-full inline-flex items-center justify-between gap-2 rounded-xl bg-white border border-soft px-4 py-3 text-sm font-bold text-slate-800 shadow-sm"
      >
        <span className="inline-flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-[color:var(--brand-blue)]" />
          Filters
          {activeFilterCount > 0 && (
            <span
              data-testid="filter-rail-active-count"
              className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[color:var(--brand-blue)] text-white text-[11px] font-black"
            >
              {activeFilterCount}
            </span>
          )}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${mobileOpen ? "rotate-180" : ""}`} />
      </button>

      <div className={`${mobileOpen ? "block mt-4" : "hidden"} lg:block lg:mt-0 space-y-5`} data-testid="filter-rail-body">
      {!schema || useFallbackStatic ? (
        staticCommon
      ) : (
        <>
          {schemaCommon.map(renderCommonField)}
          {(schemaCommon.find((f) => f.key === "min_price") ||
            schemaCommon.find((f) => f.key === "max_price")) && (
            <FilterGroup label="Price ($)">
              <div className="flex items-center gap-2">
                <input
                  data-testid={SEARCH.filterMinPrice}
                  value={state.min_price}
                  onChange={(e) => set({ min_price: e.target.value.replace(/[^0-9]/g, "") })}
                  type="text"
                  placeholder="Min"
                  className="input flex-1"
                />
                <span className="text-slate-300">—</span>
                <input
                  data-testid={SEARCH.filterMaxPrice}
                  value={state.max_price}
                  onChange={(e) => set({ max_price: e.target.value.replace(/[^0-9]/g, "") })}
                  type="text"
                  placeholder="Max"
                  className="input flex-1"
                />
              </div>
            </FilterGroup>
          )}

          {openHomeGroup}

          {!!schemaVehicle.length && (
            <div className="pt-4 mt-4 border-t border-subtle space-y-5" data-testid="filter-vehicle-group">
              {schemaVehicle.map((f) => {
                if (f.key === "make") {
                  return (
                    <FilterGroup key="make" label={f.label}>
                      <MakeSelect
                        endpoint={f.options_endpoint}
                        categorySlug={categorySlug}
                        value={state.make}
                        onChange={(v) =>
                          // Whenever make changes (to a new make, to empty,
                          // or cleared) always drop model so we don't leak
                          // an orphan model filter into the URL/request.
                          set({ make: v, model: "" })
                        }
                        testId="filter-make"
                        counts={facets?.make}
                      />
                    </FilterGroup>
                  );
                }
                if (f.key === "model") {
                  return (
                    <FilterGroup key="model" label={f.label}>
                      <ModelSelect
                        categorySlug={categorySlug}
                        make={state.make}
                        value={state.model}
                        onChange={(v) => set({ model: v })}
                      />
                    </FilterGroup>
                  );
                }
                if (f.key === "min_year" || f.key === "max_year") return null;
                return null;
              })}
              {(schemaVehicle.some((f) => f.key === "min_year") ||
                schemaVehicle.some((f) => f.key === "max_year")) && (
                <FilterGroup label="Year">
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="filter-min-year"
                      value={state.min_year}
                      onChange={(e) => set({ min_year: e.target.value.replace(/[^0-9]/g, "").slice(0, 4) })}
                      type="text"
                      inputMode="numeric"
                      placeholder="From"
                      className="input flex-1"
                    />
                    <span className="text-slate-300">—</span>
                    <input
                      data-testid="filter-max-year"
                      value={state.max_year}
                      onChange={(e) => set({ max_year: e.target.value.replace(/[^0-9]/g, "").slice(0, 4) })}
                      type="text"
                      inputMode="numeric"
                      placeholder="To"
                      className="input flex-1"
                    />
                  </div>
                </FilterGroup>
              )}
            </div>
          )}

          {!!schemaAttrs.length && (
            <div className="pt-4 mt-4 border-t border-subtle space-y-5" data-testid="filter-attributes-group">
              {schemaAttrs.map((f) => {
                const testId = `filter-${slugify(f.param || f.key)}`;
                // A facet key is count-bearing ONLY when its map has at
                // least one key. Empty `{}` from backend must NOT dim
                // options (regression from iteration_60).
                const counts = facets?.[f.key];
                const hasCounts = counts && Object.keys(counts).length > 0;
                return (
                  <FilterGroup key={f.key} label={f.label || f.key}>
                    <ChipsMulti
                      testId={testId}
                      value={state[f.param] || ""}
                      onChange={(v) => set({ [f.param]: v })}
                      options={f.options || []}
                      counts={hasCounts ? counts : undefined}
                      disabledZero={hasCounts}
                    />
                  </FilterGroup>
                );
              })}
            </div>
          )}
          {!!schemaRanges.length && (
            <div className="pt-4 mt-4 border-t border-subtle space-y-5" data-testid="filter-ranges-group">
              {schemaRanges.map((f) => (
                <FilterGroup key={f.key} label={f.label || f.key}>
                  <div className="flex items-center gap-2">
                    <input
                      data-testid={`filter-min-${slugify(f.key)}`}
                      value={state[f.param_min] || ""}
                      onChange={(e) => set({ [f.param_min]: e.target.value.replace(/[^0-9.]/g, "") })}
                      type="text" inputMode="numeric" placeholder="Min" className="input flex-1"
                    />
                    <span className="text-slate-300">—</span>
                    <input
                      data-testid={`filter-max-${slugify(f.key)}`}
                      value={state[f.param_max] || ""}
                      onChange={(e) => set({ [f.param_max]: e.target.value.replace(/[^0-9.]/g, "") })}
                      type="text" inputMode="numeric" placeholder="Max" className="input flex-1"
                    />
                  </div>
                </FilterGroup>
              ))}
            </div>
          )}

          {(!!schemaHistory.length || isVehicle) && (
            <div className="pt-4 mt-4 border-t border-subtle space-y-2.5" data-testid="filter-history-group">
              {!!schemaHistory.length && (
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Import history</div>
              )}
              {schemaHistory.map((f) => (
                <label key={f.param} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`filter-history-${slugify(f.param)}`}
                    checked={(state[f.param] || "") === f.value}
                    onChange={(e) => set({ [f.param]: e.target.checked ? f.value : "" })}
                    className="accent-[color:var(--brand-blue)]"
                  />
                  {f.label}
                </label>
              ))}
              {isVehicle && (
                <>
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="filter-4wd-only"
                      checked={(state["attr.Wheel Drive"] || "") !== ""}
                      onChange={(e) => set({ "attr.Wheel Drive": e.target.checked ? "4WD,AWD" : "" })}
                      className="accent-[color:var(--brand-blue)]"
                    />
                    4WD only
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="filter-bg-check"
                      checked={state.bg_check === "true"}
                      onChange={(e) => set({ bg_check: e.target.checked ? "true" : "" })}
                      className="accent-[color:var(--brand-blue)]"
                    />
                    Background Check available
                  </label>
                </>
              )}
            </div>
          )}

          {sellerTypeFilter && (
            <div className="pt-4 mt-4 border-t border-subtle" data-testid="filter-seller-type-group">
              <FilterGroup label="Seller type">
                <div className="flex flex-wrap gap-1.5" data-testid="filter-seller-type">
                  {(sellerTypeFilter.options || []).map((o) => {
                    const on = state.seller_type === o;
                    return (
                      <button
                        key={o}
                        type="button"
                        aria-pressed={on}
                        data-testid={`filter-seller-type-${o}`}
                        onClick={() => set({ seller_type: on ? "" : o })}
                        className={`rounded-full px-2.5 py-1 text-[11px] font-semibold border transition-colors ${
                          on
                            ? "bg-[color:var(--brand-blue)] text-white border-transparent"
                            : "bg-white text-slate-700 border-soft hover:border-[color:var(--brand-blue)]"
                        }`}
                      >
                        {o === "dealer" ? "Dealer" : "Private seller"}
                      </button>
                    );
                  })}
                </div>
              </FilterGroup>
            </div>
          )}
        </>
      )}

      <RailFooter state={state} categorySlug={categorySlug} schema={schema} />
      <div className="pt-2 flex flex-col gap-2">
        <button
          type="button"
          data-testid={SEARCH.clearFilters}
          onClick={clear}
          className="w-full rounded-full py-2 text-sm font-semibold text-slate-500 hover:text-red-500 transition-colors inline-flex items-center justify-center gap-1"
        >
          <X className="w-3.5 h-3.5" /> Clear all
        </button>
      </div>

      <style>{`.input{width:100%;background:#fff;border:1px solid var(--border-soft);border-radius:12px;padding:.55rem .8rem;font-size:.875rem;color:var(--text-primary);}
      .input:focus{outline:none;border-color:var(--brand-blue);box-shadow:0 0 0 3px rgba(28,169,224,.12);}
      .input:disabled{background:#f8fafc;color:#94a3b8;cursor:not-allowed;}`}</style>
      </div>
    </aside>
  );
}

function emptyState() {
  return {
    category: "",
    q: "",
    region: "",
    city: "",
    area: "",
    min_price: "",
    max_price: "",
    condition: "",
    sort: "",
    make: "",
    model: "",
    min_year: "",
    max_year: "",
    open_home: "",
    seller_type: "",
    bg_check: "",
  };
}

function paramsToState(params) {
  const s = emptyState();
  Object.keys(s).forEach((k) => {
    s[k] = params.get(k) || "";
  });
  // Preserve every attr.* / attrmin.* / attrmax.* param verbatim in state so
  // schema-driven attribute + range filters round-trip through URL edits.
  params.forEach((v, k) => {
    if (k.startsWith("attr.") || k.startsWith("attrmin.") || k.startsWith("attrmax.")) s[k] = v;
  });
  return s;
}

function getAreaOptions(schema, region, city) {
  // Areas aren't currently declared in filter-schema options — future-proof
  // by checking if the backend ever adds them under common_filters.area.options.
  const area = (schema?.common_filters || []).find((f) => f.key === "area");
  if (Array.isArray(area?.options)) return area.options;
  return [];
}

/** Map the rail's local state to /classifieds/listings request params. */
function stateToListingParams(state, categorySlug) {
  const p = { country: "NZ", limit: 1 };
  if (categorySlug || state.category) p.category = categorySlug || state.category;
  Object.entries(state).forEach(([k, v]) => {
    if (!v || k === "category" || k === "open_home") return;
    p[k] = v;
  });
  if (state.open_home === "yes" || state.open_home === "weekend") p.open_home = state.open_home;
  if (p.sort === "distance") delete p.sort;
  return p;
}

/**
 * Iter-531 — sticky rail footer: debounced live "View N results" count +
 * "Save search" alerts (POST /classifieds/saved-searches).
 */
function RailFooter({ state, categorySlug, schema }) {
  const [debounced, setDebounced] = useState(state);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(state), 350);
    return () => clearTimeout(t);
  }, [state]);

  const countParams = useMemo(
    () => stateToListingParams(debounced, categorySlug),
    [debounced, categorySlug],
  );
  const countQ = useQuery({
    queryKey: ["refine-count", countParams],
    queryFn: async () =>
      (await sharedApi.get("/classifieds/listings", { params: countParams })).data?.total ?? 0,
    placeholderData: (prev) => prev,
  });

  const saveSearch = async () => {
    setSaving(true);
    try {
      const body = {};
      if (categorySlug || state.category) body.category_slug = categorySlug || state.category;
      if (state.q) body.q = state.q;
      ["region", "city", "area", "make", "model", "seller_type"].forEach((k) => {
        if (state[k]) body[k] = state[k];
      });
      ["min_price", "max_price", "min_year", "max_year"].forEach((k) => {
        if (state[k]) body[k] = Number(state[k]);
      });
      if (state.condition) body.condition = state.condition.split(",").filter(Boolean);
      const attrs = {};
      const ranges = {};
      Object.entries(state).forEach(([k, v]) => {
        if (!v) return;
        if (k.startsWith("attr.")) attrs[k.slice(5)] = String(v).split(",").filter(Boolean);
        if (k.startsWith("attrmin.")) ranges[k.slice(8)] = { ...(ranges[k.slice(8)] || {}), min: Number(v) };
        if (k.startsWith("attrmax.")) ranges[k.slice(8)] = { ...(ranges[k.slice(8)] || {}), max: Number(v) };
      });
      if (Object.keys(attrs).length) body.attrs = attrs;
      if (Object.keys(ranges).length) body.ranges = ranges;
      if (!body.q && !body.category_slug) {
        toast.error("Pick a category or add a keyword first.");
        return;
      }
      body.name = [state.make, schema?.category?.name].filter(Boolean).join(" ") || undefined;
      const { data } = await sharedApi.post("/classifieds/saved-searches", body);
      toast.success(
        data?.already_saved
          ? "You already saved this search — alerts are on."
          : "Search saved — we'll alert you the moment a matching ad goes live.",
      );
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) toast.error("Sign in to save searches and get alerts.");
      else toast.error(err?.response?.data?.detail || "Couldn't save this search.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sticky bottom-0 -mx-1 px-1 pt-2 pb-1 bg-[color:var(--bg-page,#f8fafc)]/95 backdrop-blur space-y-2" data-testid="refine-footer">
      <div
        className="w-full rounded-full bg-[color:var(--brand-blue)] text-white text-sm font-bold py-2.5 text-center"
        data-testid="refine-view-results"
      >
        {countQ.isFetching && countQ.data == null
          ? "Counting…"
          : `View ${Number(countQ.data ?? 0).toLocaleString("en-NZ")} result${(countQ.data ?? 0) === 1 ? "" : "s"}`}
      </div>
      <button
        type="button"
        onClick={saveSearch}
        disabled={saving}
        data-testid="refine-save-search"
        className="w-full rounded-full border border-[color:var(--brand-blue)] text-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-wash,#e0f2fe)] text-sm font-bold py-2.5 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
      >
        <BellPlus className="w-4 h-4" /> {saving ? "Saving…" : "Save search"}
      </button>
    </div>
  );
}
